import { useEffect, useRef, useState, type ReactNode } from "react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useQueryClient } from "@tanstack/react-query"
import {
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Circle,
  Loader2,
} from "lucide-react"
import { toast } from "sonner"

import { processRunControllerGetQueryKey } from "@/client/@tanstack/react-query.gen"
import type { ProcessRunDto, TrainingSessionDto } from "@/client/types.gen"
import { FeedbackPanel } from "@/components/feedback-panel"
import { VorleseButton } from "@/components/vorlese-button"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import { SHOW_IDD_TIME } from "@/lib/feature-flags"
import { coachPossessive, useCoachById } from "@/lib/use-coach"
import { formatHMS } from "@/lib/idd/active-time"
import { sessionForUnit, unitSectionId } from "@/lib/run-phases"
import { useGenerateFeedback, usePersonas, useProcessRun } from "@/lib/queries"
import {
  EINHEIT_LABELS,
  NACHBEREITUNG_FRAGEN,
  einheitDefinition,
  istGesamtbild,
  type TrainingsEinheit,
} from "@nl/shared"

const STATUS_LABELS: Record<ProcessRunDto["status"], string> = {
  active: "Aktiv",
  completed: "Abgeschlossen",
  abandoned: "Abgebrochen",
}

/** Same field labels as the preparation checkpoint in the run player. */
const PREP_LABELS: Record<string, string> = {
  alter: "Alter",
  familienstand: "Familienstand",
  kinder: "Kinder",
  beruf: "Beruf, Arbeitgeber",
  einkommen: "Einkommen",
  bestandsvorsorge: "Bestandsverträge / Anlagen / Depot",
  wohnsituation: "Wohnsituation",
}

/**
 * Editorial view switcher (mono uppercase labels on a rule, primary underline
 * for the active view) — the shadcn Tabs pill reads off-brand here.
 */
function ViewSwitch({
  value,
  onChange,
  action,
}: {
  value: "feedback" | "transcript"
  onChange: (value: "feedback" | "transcript") => void
  /** Bedienung rechts in der Reiterzeile, etwa das Vorlesen des Feedbacks. */
  action?: ReactNode
}) {
  const options = [
    { value: "feedback" as const, label: "Feedback" },
    { value: "transcript" as const, label: "Transkript" },
  ]
  return (
    <div className="flex items-center justify-between gap-3 border-b border-[var(--rule)]">
      <div role="tablist" className="flex items-center gap-5">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={value === option.value}
            onClick={() => onChange(option.value)}
            className={cn(
              "-mb-px border-b-2 pb-2.5 text-[11.5px] font-bold tracking-[0.06em] uppercase transition-colors",
              value === option.value
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
      {action}
    </div>
  )
}

/** Highlights the section currently in view, for the table of contents. */
function useActiveSection(ids: string[]): string | null {
  const [active, setActive] = useState<string | null>(null)
  const key = ids.join(",")
  useEffect(() => {
    if (!ids.length) return
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible[0]) setActive(visible[0].target.id)
      },
      // A section counts as "active" while it crosses the upper third of the
      // viewport — feels right for long, stacked sections.
      { rootMargin: "-15% 0px -65% 0px" }
    )
    for (const id of ids) {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    }
    return () => observer.disconnect()
  }, [key])
  return active
}

/**
 * Jumps to the unit named in the URL hash, once it exists.
 *
 * The router's own hash handling fires on navigation, when the run is still
 * loading and the unit sections are not in the DOM yet — arriving from the
 * debrief would land at the top of the page. `ready` is the signal that the
 * sections have rendered.
 */
function useHashJump(ready: boolean) {
  const gesprungen = useRef(false)
  useEffect(() => {
    if (!ready || gesprungen.current) return
    const id = window.location.hash.slice(1)
    if (!id) return
    const el = document.getElementById(id)
    if (!el) return
    gesprungen.current = true
    el.scrollIntoView({ block: "start" })
  }, [ready])
}

/** Sticky table of contents on the right — jump marks for the unit sections. */
function TocNav({
  units,
  activeId,
}: {
  units: TrainingsEinheit[]
  activeId: string | null
}) {
  return (
    <nav aria-label="Inhalt" className="sticky top-8">
      <div className="text-[11px] font-bold tracking-[0.06em] text-muted-foreground uppercase">
        Inhalt
      </div>
      <ul className="mt-3 border-l border-[var(--rule)]">
        {units.map((phase, i) => {
          const id = unitSectionId(phase)
          const active = activeId === id
          return (
            <li key={phase}>
              <a
                href={`#${id}`}
                onClick={(e) => {
                  e.preventDefault()
                  document
                    .getElementById(id)
                    ?.scrollIntoView({ behavior: "smooth", block: "start" })
                }}
                className={cn(
                  "-ml-px block border-l-2 py-1.5 pl-3 text-[13.5px] leading-snug transition-colors",
                  active
                    ? "border-primary font-medium text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                )}
              >
                <span className="text-[11px] font-bold tracking-[0.06em] text-muted-foreground/70 uppercase">
                  {String(i + 1).padStart(2, "0")}
                </span>{" "}
                {einheitDefinition(phase).shortLabel}
              </a>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

/**
 * Read-only bubble list over the roleplay transcript. Deliberately not the
 * interactive RoleplayChat — this is an archive view, not a conversation.
 */
function TranscriptView({
  session,
  personaName,
}: {
  session: TrainingSessionDto
  personaName: string
}) {
  if (session.messages.length === 0) {
    return (
      <p className="py-6 text-center text-[14px] text-muted-foreground">
        Kein Gesprächsprotokoll vorhanden.
      </p>
    )
  }
  return (
    <div className="flex flex-col gap-4 py-4">
      {session.messages.map((m, i) => {
        const isUser = m.role === "user"
        return (
          <div
            key={m.id ?? i}
            className={isUser ? "flex justify-end" : "flex justify-start"}
          >
            <div className="max-w-[80%] space-y-1">
              <div className="text-[11px] font-bold tracking-[0.06em] text-muted-foreground uppercase">
                {isUser ? "Beraterin" : personaName}
              </div>
              <div
                className={
                  isUser
                    ? "rounded-[10px] rounded-br-[2px] bg-primary px-4 py-3 text-[14px] leading-[1.55] whitespace-pre-wrap text-primary-foreground"
                    : "rounded-[10px] rounded-bl-[2px] border border-[var(--rule)] bg-card px-4 py-3 text-[14px] leading-[1.55] whitespace-pre-wrap"
                }
              >
                {m.content}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

/**
 * A unit that was finished but never reflected on.
 *
 * Es gibt eine Auswertung, sie ist aber still aus dem Protokoll entstanden und
 * damit keine gemeinsam erarbeitete Bilanz. Sie trägt die
 * Kompetenz-Durchschnitte und den IDD-Nachweis; als Ergebnis vorgelegt wird sie
 * nicht, sonst wäre die fertige Auswertung durch die Hintertür zurück. Wer sie
 * sehen will, klappt sie auf.
 */
function OhneReflexion({
  feedback,
  coachName,
  coachStyleId,
}: {
  feedback: NonNullable<TrainingSessionDto["feedback"]>
  coachName?: string
  coachStyleId?: string
}) {
  return (
    <div className="space-y-4">
      <p className="text-[14px] leading-relaxed text-muted-foreground">
        Zu dieser Einheit gibt es kein Reflexionsgespräch. Das Feedback entsteht
        im Gespräch mit dem Coach — hier stehen nur die Beobachtungen, die für
        den Gesamtüberblick aufgenommen wurden.
      </p>
      <details className="group rounded-xl border border-[var(--rule)] bg-card">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 rounded-xl px-6 py-4 transition-colors hover:bg-accent/40 [&::-webkit-details-marker]:hidden">
          <span className="text-[11.5px] font-bold tracking-[0.06em] text-foreground/70 uppercase transition-colors group-hover:text-foreground">
            Beobachtungen ansehen
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
        </summary>
        <div className="border-t border-[var(--rule)] px-6 py-5">
          <FeedbackPanel
            feedback={feedback}
            coachName={coachName}
            coachStyleId={coachStyleId}
          />
        </div>
      </details>
    </div>
  )
}

/**
 * Recovery for a unit whose evaluation failed during the run.
 *
 * Erzeugt bewusst nur die interne Beobachtung, nicht die Bilanz: die entsteht
 * ausschliesslich im Reflexionsgespräch. Der Endpunkt ist idempotent, ein
 * zweiter Klick kostet also nichts.
 */
function MissingFeedback({
  session,
  runId,
}: {
  session: TrainingSessionDto
  runId: string
}) {
  const qc = useQueryClient()
  const generate = useGenerateFeedback()

  // The server requires at least one exchange to grade.
  if (session.messages.length < 2) {
    return (
      <p className="py-6 text-center text-[14px] text-muted-foreground">
        Für diese Einheit liegt kein auswertbares Gespräch vor.
      </p>
    )
  }

  return (
    <div className="flex flex-col items-center gap-3 py-6 text-center">
      <p className="text-[14px] text-muted-foreground">
        Für diese Einheit fehlen die Beobachtungen, die in den Gesamtüberblick
        eingehen.
      </p>
      <Button
        size="sm"
        disabled={generate.isPending}
        onClick={() =>
          generate.mutate(session.id, {
            onSuccess: () =>
              void qc.invalidateQueries({
                queryKey: processRunControllerGetQueryKey({
                  path: { id: runId },
                }),
              }),
            onError: () =>
              toast.error(
                "Feedback konnte nicht erstellt werden. Bitte versuche es erneut."
              ),
          })
        }
      >
        {generate.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
        Beobachtungen nachtragen
      </Button>
    </div>
  )
}

/** The preparation checkpoint's result — no session, just the case state. */
function PrepResultCard({ run }: { run: ProcessRunDto }) {
  const prep = run.caseState.preparation
  if (!prep) {
    return (
      <p className="text-[14px] text-muted-foreground">
        Die Gesprächsvorbereitung wurde in diesem Durchlauf nicht abgeschlossen.
      </p>
    )
  }
  return (
    <div className="space-y-4">
      <div className="grid gap-2 sm:grid-cols-2">
        {prep.checklist.map((item) => (
          <div
            key={item.key}
            className="flex items-center gap-2 rounded-md border border-[var(--rule)] px-3 py-2 text-[14px]"
          >
            {item.checked ? (
              <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
            ) : (
              <Circle className="h-4 w-4 shrink-0 text-muted-foreground/50" />
            )}
            <span>{PREP_LABELS[item.key] ?? item.key}</span>
          </div>
        ))}
      </div>
      {prep.goal && (
        <div>
          <div className="text-[11px] font-bold tracking-[0.06em] text-muted-foreground uppercase">
            Gesprächsziel
          </div>
          <p className="mt-1 text-[14.5px] leading-relaxed">{prep.goal}</p>
        </div>
      )}
    </div>
  )
}

/**
 * The follow-up work in hindsight — like the preparation sheet, without a
 * session, straight from the case state.
 *
 * A card of its own rather than `PrepResultCard`: both units carry no feedback
 * but show different sheets. Without the split, the preparation's checklist
 * would stand under the "Nachbereitung" heading.
 */
function NacharbeitResultCard({ run }: { run: ProcessRunDto }) {
  const antworten = new Map(
    (run.caseState.nachbereitung?.antworten ?? []).map((a) => [a.key, a.value])
  )
  const eintraege = NACHBEREITUNG_FRAGEN.flatMap((frage) => {
    const antwort = (antworten.get(frage.key) ?? "").trim()
    return antwort ? [{ ...frage, antwort }] : []
  })

  if (eintraege.length === 0) {
    return (
      <p className="text-[14px] text-muted-foreground">
        Die Nachbereitung wurde in diesem Durchlauf nicht ausgefüllt.
      </p>
    )
  }

  return (
    <dl className="space-y-4">
      {eintraege.map((eintrag) => (
        <div key={eintrag.key}>
          <dt className="text-[11px] font-bold tracking-[0.06em] text-muted-foreground uppercase">
            {eintrag.kurz}
          </dt>
          <dd className="mt-1 text-[14.5px] leading-relaxed">
            {eintrag.antwort}
          </dd>
        </div>
      ))}
    </dl>
  )
}

function EinheitSection({
  run,
  phase,
  index,
  persona,
}: {
  run: ProcessRunDto
  phase: TrainingsEinheit
  index: number
  persona:
    NonNullable<ReturnType<typeof usePersonas>["data"]>[number] | undefined
}) {
  const session = einheitDefinition(phase).hatFeedback
    ? sessionForUnit(run, phase)
    : undefined
  const personaName = persona?.name ?? "Kundin"
  const coach = useCoachById(run.coachStyleId)
  const coachName = coachPossessive(coach)
  const [view, setView] = useState<"feedback" | "transcript">("feedback")

  return (
    <Card
      id={unitSectionId(phase)}
      className="scroll-mt-6 gap-0 border-[var(--rule)] p-0"
    >
      <div className="flex items-center justify-between gap-3 border-b border-[var(--rule)] px-6 py-4">
        <h2 className="flex items-center gap-2 font-display text-[16px] leading-none font-medium tracking-tight">
          <span className="text-[11.5px] leading-none font-bold tracking-[0.06em] text-primary uppercase">
            Einheit {index + 1}
          </span>
          {EINHEIT_LABELS[phase]}
        </h2>
        {SHOW_IDD_TIME && session && (
          <span className="shrink-0 text-[11.5px] font-bold tracking-[0.06em] text-muted-foreground uppercase">
            {formatHMS(session.activeSeconds)} aktiv
          </span>
        )}
      </div>

      <div className="px-6 py-4">
        {istGesamtbild(phase) ? (
          <NacharbeitResultCard run={run} />
        ) : !einheitDefinition(phase).hatFeedback ? (
          <PrepResultCard run={run} />
        ) : !session ? (
          <p className="text-[14px] text-muted-foreground">
            Diese Einheit wurde noch nicht trainiert.
          </p>
        ) : (
          <div>
            <ViewSwitch
              value={view}
              onChange={setView}
              action={
                view === "feedback" && session.feedback ? (
                  <VorleseButton
                    feedback={session.feedback}
                    voice={coach?.voice}
                    sessionId={session.id}
                    className="mb-1.5"
                  />
                ) : undefined
              }
            />
            <div className="pt-4">
              {view === "transcript" ? (
                <TranscriptView session={session} personaName={personaName} />
              ) : session.feedback?.herkunft === "dialog" ? (
                // Deliberately no per-unit coach here: the review is an
                // archive, the conversational surface is the run-wide coach
                // in the debrief.
                <FeedbackPanel
                  feedback={session.feedback}
                  coachName={coachName}
                  coachStyleId={run.coachStyleId}
                />
              ) : session.feedback ? (
                <OhneReflexion
                  feedback={session.feedback}
                  coachName={coachName}
                  coachStyleId={run.coachStyleId}
                />
              ) : (
                <MissingFeedback session={session} runId={run.id} />
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}

function RunReview() {
  const { runId } = Route.useParams()
  const { data: run, isLoading } = useProcessRun(runId)
  const { data: personas = [] } = usePersonas()
  const persona = personas.find((p) => p.id === run?.personaId)
  // Before the early return — hooks must run unconditionally.
  const activeSection = useActiveSection(
    ((run?.units ?? []) as TrainingsEinheit[]).map(unitSectionId)
  )
  useHashJump(!!run)

  if (isLoading || !run) {
    return (
      <div className="flex items-center gap-3 p-8 text-[14px] text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Durchlauf wird geladen…
      </div>
    )
  }

  const units = run.units as TrainingsEinheit[]
  const startedAt = new Date(run.startedAt).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  })

  return (
    <div className="space-y-6">
      <header className="reveal border-b border-[var(--rule)] pb-6">
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <h1 className="font-display text-[28px] leading-[1.1] font-medium tracking-tight">
            {persona ? `Training mit ${persona.name}` : "Dein Durchlauf"}
          </h1>
          <Badge variant={run.status === "completed" ? "default" : "secondary"}>
            {STATUS_LABELS[run.status]}
          </Badge>
        </div>
        <p className="mt-2 text-[14px] text-muted-foreground">
          {startedAt} · {units.length}{" "}
          {units.length === 1 ? "Einheit" : "Einheiten"}
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {/* Über einer einzigen Einheit fasst das Debrief nichts zusammen. */}
          {run.status === "completed" && units.length > 1 && (
            <Button asChild>
              <Link to="/durchlauf/$runId/debrief" params={{ runId }}>
                Zum Gesamt-Debrief
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          )}
          {run.status === "active" && (
            <Button asChild>
              <Link to="/durchlauf/$runId" params={{ runId }}>
                Weiter trainieren
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          )}
        </div>
      </header>

      {/* No items-start: the aside must stretch to the full column height,
          otherwise the sticky nav has no room to travel while scrolling. */}
      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_190px] lg:gap-10">
        <div className="space-y-4">
          {units.map((phase, i) => (
            <EinheitSection
              key={phase}
              run={run}
              phase={phase}
              index={i}
              persona={persona}
            />
          ))}
        </div>
        <aside className="hidden lg:block">
          <TocNav units={units} activeId={activeSection} />
        </aside>
      </div>
    </div>
  )
}

export const Route = createFileRoute("/_app/durchlauf/$runId_/review")({
  component: RunReview,
})
